<!--
  Odd Boost Badge — PLAYER-VISIBLE.

  Small player-facing marker rendered next to an outcome value when the
  odd was multiplied by an admin-configured boost rule at the API side.

  Contract from rocky-api (Fase 2, K.17 payload extension):
    - `odd.boostApplied === true`
    - `odd.boostPercent`  = the % applied (e.g. 5 for +5%)

  When either field is missing the badge is hidden — no DOM emitted. This
  keeps the outcome rendering identical for non-boosted odds and for skins
  where `SkinConfig.enableBoostOdds === false` (in that case the resolver
  server-side skips entirely and the payload never carries these fields).

  ─── Visual design ────────────────────────────────────────────────────
  Two visual modes decided by `showPercent`. Both render the SAME `+N%`
  textual signal — no missile / rocket / lightning icons (dropped 2026-08
  after ops feedback that they read arcade-y). The visual difference lives
  entirely in the CSS class variant.

    showPercent = true  (used in the cart / bet slip):
      Horizontal "pill" capsule with "+N%" text. Sits inline next to the
      served price. Pink/orange gradient so the boost signal reads as
      warm/positive against the neutral cart background.

    showPercent = false (used inline in market cells):
      Diagonal green "sale ribbon" that fills the top-right corner of the
      odd cell (clip-path triangle). Text "+N%" rotated 45° along the
      hypotenuse. Green gradient because in the sportsbook color language
      green consistently means "good for the player" (positive EV), and
      matches the widget's `.price-compare` "was → now" callout so both
      surfaces feel like one product. No animation — pulsing was
      distracting on dense market grids.

  Both modes render the SAME inline element from Vue's perspective — the
  CSS class variant decides the layout. The absolute positioning is
  scoped to `.odd-boost-badge--corner` so nothing else in the codebase
  is disturbed.

  ─── Positioning note ─────────────────────────────────────────────────
  The corner variant uses `position: absolute` and relies on the parent
  cell having `position: relative` (or default static, in which case it
  falls back to the nearest positioned ancestor). Rocky's odd cells
  generally already carry `position: relative` for type_change animations,
  so no additional CSS is needed on the callsite. If a specific cell
  ends up mis-positioning the badge, wrap the odd value in a
  `<span class="odd-boost-anchor">` — the badge finds a positioned
  ancestor via nearest-ancestor rules.
-->
<template>
  <span v-if="visible"
        class="odd-boost-badge"
        :class="[
          `odd-boost-badge--${size}`,
          showPercent ? 'odd-boost-badge--inline' : 'odd-boost-badge--corner'
        ]"
        :title="tooltip"
        aria-label="Boosted odds">
    <!-- Both variants now show the "+N%" text — the missile / rocket icon
         was dropped after ops feedback it read as a game / launcher gimmick
         rather than a professional promo badge. The visual differentiation
         between the two variants lives entirely in the CSS below:
           - inline pill (cart) → horizontal capsule with the text
           - corner ribbon (market cells) → diagonal triangle with the
             text rotated 45° along the hypotenuse, like a classic
             "sale" / "promo" corner ribbon. -->
    <span class="odd-boost-badge__pct">+{{ formattedPercent }}%</span>
  </span>
</template>

<script>
export default {
    name: 'OddBoostBadge',
    props: {
        odd: {type: Object, default: null},
        size: {
            type: String,
            default: 'sm',
            validator: v => ['sm', 'md'].includes(v)
        },
        // When true → pill mode (icon + "+X%" text). When false → corner
        // ribbon (icon only, absolute-positioned in the cell top-right).
        showPercent: {type: Boolean, default: true}
    },
    computed: {
        visible() {
            if (!this.odd) return false;
            if (this.odd.boostApplied !== true) return false;
            // A rule with pct=null shouldn't happen (write-time cap rejects
            // 0/negative), but defense-in-depth: don't render the badge with
            // an unreadable "+null%".
            const pct = Number(this.odd.boostPercent);
            return Number.isFinite(pct) && pct > 0;
        },
        formattedPercent() {
            const pct = Number(this.odd.boostPercent);
            return Number.isInteger(pct) ? String(pct) : pct.toFixed(1).replace(/\.0$/, '');
        },
        tooltip() {
            return `Boost +${this.formattedPercent}%`;
        }
    }
};
</script>

<style scoped>
/* ─── Inline pill variant (cart / bet slip) ───────────────────────── */
.odd-boost-badge--inline {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 6px;
    border-radius: 10px;
    background: var(--color-primary);
    color: #ffffff;
    font-weight: 800;
    line-height: 1;
    vertical-align: middle;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    pointer-events: none;
    letter-spacing: 0.3px;
    font-variant-numeric: tabular-nums;
}
.odd-boost-badge--inline.odd-boost-badge--sm {
    font-size: var(--font-size-md);
    height: 16px;
}
.odd-boost-badge--inline.odd-boost-badge--md {
    font-size: var(--font-size-2xl);
    height: 20px;
    padding: 3px 8px;
}

/* ─── Corner ribbon variant (market cells) ─────────────────────────── */
/* Diagonal "sale-style" ribbon anchored in the top-right corner of the
   odd cell. Rationale for the visual choice (2026-08 UX review):
     - Was a red circular badge with a missile → felt game-y / arcade,
       ops asked for a more professional signal.
     - Now: green gradient triangle carved out with clip-path, showing
       "+N%" rotated 45° along the hypotenuse — matches the classic
       corner-promo pattern (Amazon deals, marketplace sales, hotel
       booking discounts) that users already read as "this item has a
       special offer".

   Absolute positioning still relies on the parent cell being positioned
   (rocky's `.value` / `.odd-cell` are `position:relative` per stylenew.css).
   The triangle fills the corner exactly so the clip-path DOES NOT
   overflow the cell — `overflow:hidden` parents are safe. */
.odd-boost-badge--corner {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 3;
    background: var(--color-primary);
    color: #fff;
    /* Triangle: TR corner filled, BL corner clipped out. The three
       points (top-right → top-left → bottom-right) define the diagonal
       hypotenuse the "+N%" text sits on. */
    clip-path: polygon(100% 0, 0 0, 100% 100%);
    pointer-events: none;
    /* Subtle inner shadow along the hypotenuse for a lifted ribbon feel
       (drop-shadow on the clipped shape, applied via filter). */
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
}
.odd-boost-badge--corner.odd-boost-badge--sm {
    width: 30px;
    height: 30px;
}
.odd-boost-badge--corner.odd-boost-badge--md {
    width: 38px;
    height: 38px;
}
/* "+N%" text rotated 45° so it reads along the diagonal edge of the
   triangle. Positioned near the outer edge of the corner so it stays
   inside the visible clip area. `pointer-events:none` on parent keeps
   the odd click through. */
.odd-boost-badge--corner .odd-boost-badge__pct {
    position: absolute;
    top: 4px;
    right: -6px;
    width: 34px;
    text-align: center;
    transform: rotate(45deg);
    transform-origin: center;
    font-weight: 900;
    letter-spacing: 0.02em;
    line-height: 1;
}
.odd-boost-badge--corner.odd-boost-badge--sm .odd-boost-badge__pct {
    font-size: var(--font-size-xxs);
}
.odd-boost-badge--corner.odd-boost-badge--md .odd-boost-badge__pct {
    font-size: var(--font-size-md);
    top: 6px;
    right: -6px;
    width: 42px;
}

.odd-boost-badge__pct {
    font-variant-numeric: tabular-nums;
}
</style>
